<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useCentertelTags } from 'dashboard/composables/useCentertelTags';

const store = useStore();
const { userPermissions, isAdmin, currentChat, currentUser } = useCentertelTags();

const currentRole = computed(() => store.getters.getCurrentRole);
const customRoleId = computed(() => store.getters.getCurrentCustomRoleId);
const customRoles = computed(() => store.getters['customRole/getCustomRoles'] || []);
const customRoleName = computed(() => {
  if (!customRoleId.value) return null;
  const role = customRoles.value.find(r => r.id === customRoleId.value);
  return role?.name || null;
});
const isSupervisor = computed(() =>
  (currentRole.value === 'agent' && customRoleName.value === 'Supervisor') ||
  document.body.classList.contains('ct-supervisor')
);
const isVendor = computed(() => currentRole.value === 'agent' && !isSupervisor.value);

onMounted(() => { store.dispatch('customRole/getCustomRole'); });

const isMine = computed(() => {
  const assignee = currentChat.value?.meta?.assignee;
  return assignee && assignee.id === currentUser.value?.id;
});
const isResolved = computed(() => currentChat.value?.status === 'resolved');
const isPending = computed(() => currentChat.value?.status === 'pending');
const hasAssignee = computed(() => !!currentChat.value?.meta?.assignee);

const showButtons = computed(() => {
  if (isAdmin.value) return true;
  if (isSupervisor.value) return true;
  if (isVendor.value && isMine.value) return true;
  return false;
});

// ── Dropdown states ──
const showAgentsDropdown = ref(false);
const showTeamsDropdown = ref(false);
const agents = ref([]);
const teams = ref([]);
const agentSearch = ref('');

const filteredAgents = computed(() => {
  const search = agentSearch.value.toLowerCase();
  return agents.value
    .filter(a => a.id !== currentUser.value?.id)
    .filter(a => !search || a.name.toLowerCase().includes(search))
    .slice(0, 10);
});

const loadAgents = async () => {
  if (agents.value.length) return;
  await store.dispatch('agents/get');
  agents.value = store.getters['agents/getAgents'] || [];
};

const loadTeams = async () => {
  if (teams.value.length) return;
  await store.dispatch('teams/get');
  teams.value = store.getters['teams/getTeams'] || [];
};

// ── Ações ──
const transferToAgent = async (agentId) => {
  showAgentsDropdown.value = false;
  agentSearch.value = '';
  try {
    await store.dispatch('assignAgent', {
      conversationId: currentChat.value.id,
      agentId,
    });
    // Se pendente, mudar para open
    if (isPending.value) {
      await store.dispatch('toggleStatus', {
        conversationId: currentChat.value.id,
        status: 'open',
      });
    }
    useAlert('Transferido!');
  } catch {
    useAlert('Erro ao transferir');
  }
};

const transferToTeam = async (teamId) => {
  showTeamsDropdown.value = false;
  try {
    await store.dispatch('assignTeam', {
      conversationId: currentChat.value.id,
      teamId,
    });
    useAlert('Transferido para o time!');
  } catch {
    useAlert('Erro ao transferir');
  }
};

const returnToQueue = async () => {
  if (!confirm('Devolver para a fila?')) return;
  try {
    await store.dispatch('assignAgent', {
      conversationId: currentChat.value.id,
      agentId: null,
    });
    // Se aberto, voltar pra pending
    if (currentChat.value?.status === 'open') {
      await store.dispatch('toggleStatus', {
        conversationId: currentChat.value.id,
        status: 'pending',
      });
    }
    useAlert('Devolvido para a fila');
  } catch {
    useAlert('Erro ao devolver');
  }
};

const reopenConversation = async () => {
  try {
    await store.dispatch('assignAgent', {
      conversationId: currentChat.value.id,
      agentId: currentUser.value.id,
    });
    await store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: 'open',
    });
    useAlert('Atendimento reaberto!');
  } catch {
    useAlert('Erro ao reabrir');
  }
};

const resolveConversation = async () => {
  try {
    await store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status: 'resolved',
    });
    useAlert('Atendimento finalizado!');
  } catch {
    useAlert('Erro ao finalizar');
  }
};

// ── Macros ──
const showMacrosDropdown = ref(false);
const macrosLoading = ref(false);
const macroRunning = ref(false);
const macros = computed(() => store.getters['macros/getAllMacros'] || []);

const toggleMacros = async () => {
  showMacrosDropdown.value = !showMacrosDropdown.value;
  if (showMacrosDropdown.value) {
    macrosLoading.value = true;
    try { await store.dispatch('macros/index'); } catch { /**/ }
    finally { macrosLoading.value = false; }
  }
};

const executeMacro = async (macro) => {
  if (!currentChat.value?.id) return;
  macroRunning.value = true;
  showMacrosDropdown.value = false;
  try {
    await store.dispatch('macros/run', {
      macroId: macro.id,
      conversationId: currentChat.value.id,
    });
    useAlert(`Macro "${macro.name}" executada!`);
  } catch {
    useAlert('Erro ao executar macro');
  } finally {
    macroRunning.value = false;
  }
};

// ── Toggle ações (activity messages) ──
const hideActivity = ref(true);
const toggleActivity = () => {
  hideActivity.value = !hideActivity.value;
  document.body.classList.toggle('ct-hide-activity', hideActivity.value);
};
if (typeof document !== 'undefined') {
  document.body.classList.add('ct-hide-activity');
}

// ── Telefone do contato ──
const contactPhone = computed(() => {
  return currentChat.value?.meta?.sender?.phone_number || '';
});

// ── Atendente atual (chip no header, só supervisor/admin) ──
const assignee = computed(() => currentChat.value?.meta?.assignee || null);
const assigneeName = computed(() => assignee.value?.name || '');
const assigneeAvatar = computed(() => assignee.value?.thumbnail || assignee.value?.avatar_url || '');
const assigneeInitial = computed(() => assigneeName.value.charAt(0).toUpperCase() || '?');

// ── Custom attributes ──
const customAttrs = computed(() => {
  const parts = [];
  const convAttrs = currentChat.value?.custom_attributes || {};
  const contactAttrs = currentChat.value?.meta?.sender?.custom_attributes || {};
  Object.values(convAttrs).forEach(v => { if (v) parts.push(String(v)); });
  Object.values(contactAttrs).forEach(v => { if (v) parts.push(String(v)); });
  return parts;
});
</script>

<template>
  <div v-if="currentChat?.id" class="flex items-center gap-1.5 flex-shrink-0">

    <!-- Chip atendente — visível só para supervisor/admin -->
    <div
      v-if="(isAdmin || isSupervisor) && assignee"
      class="flex items-center gap-1.5 px-2 py-1 rounded-lg mr-1"
      style="background:rgba(31,147,255,0.10);border:1px solid rgba(31,147,255,0.20)"
    >
      <span class="text-[10px] font-semibold uppercase tracking-wide" style="color:#6b7280">Atendente</span>
      <div
        class="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
        style="background:linear-gradient(135deg,#6366f1,#8b5cf6)"
      >
        <img v-if="assigneeAvatar" :src="assigneeAvatar" class="w-full h-full object-cover" />
        <span v-else>{{ assigneeInitial }}</span>
      </div>
      <span class="text-xs font-semibold text-n-slate-12 max-w-[100px] truncate">{{ assigneeName }}</span>
    </div>

    <!-- Toggle ações (olho) -->
    <button
      v-tooltip="hideActivity ? 'Mostrar ações' : 'Esconder ações'"
      class="centertel-hdr-btn"
      :class="hideActivity ? 'text-gray-400 border-gray-300' : 'text-blue-600 border-blue-300 hover:bg-blue-50'"
      style="position: relative;"
      @click="toggleActivity"
    >
      <span style="font-size: 14px;">👁</span>
      <span v-if="hideActivity" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 18px; color: #dc2626; transform: rotate(-45deg);">/</span>
    </button>

    <!-- Macros (todos os usuários) -->
    <div class="relative">
      <button
        v-tooltip="'Executar Macro'"
        class="centertel-hdr-btn text-purple-600 border-purple-300 hover:bg-purple-50"
        :class="{ 'opacity-50': macroRunning }"
        @click="toggleMacros"
      >
        ⚡
      </button>
      <div
        v-if="showMacrosDropdown"
        v-on-clickaway="() => (showMacrosDropdown = false)"
        class="absolute right-0 top-full mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg z-[200] w-[240px] max-h-[320px] overflow-hidden"
      >
        <div class="px-3 py-2 border-b border-n-weak text-[11px] font-semibold text-n-slate-8 uppercase tracking-wide">
          Macros
        </div>
        <div v-if="macrosLoading" class="px-3 py-3 text-xs text-n-slate-8 text-center">
          Carregando...
        </div>
        <div v-else-if="!macros.length" class="px-3 py-3 text-xs text-n-slate-8 text-center">
          Nenhuma macro cadastrada
        </div>
        <div v-else class="overflow-y-auto max-h-[260px]">
          <button
            v-for="macro in macros"
            :key="macro.id"
            class="flex items-center gap-2 w-full px-3 py-2 text-left text-xs hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
            :disabled="macroRunning"
            @click="executeMacro(macro)"
          >
            <span class="text-purple-500 flex-shrink-0">⚡</span>
            <span class="truncate text-n-slate-12">{{ macro.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Custom Attributes -->
    <span
      v-if="customAttrs.length"
      class="text-[11px] text-n-slate-8 italic hidden lg:inline truncate max-w-[200px]"
    >
      {{ customAttrs.join(' | ') }}
    </span>

    <!-- Resolvida: botão reabrir -->
    <template v-if="isResolved">
      <button
        v-if="isAdmin || isSupervisor"
        v-tooltip="'Reabrir Atendimento'"
        class="centertel-hdr-btn text-green-600 border-green-300 hover:bg-green-50"
        @click="reopenConversation"
      >
        ↻
      </button>
    </template>

    <!-- Aberta ou Pendente: ações (só supervisor/admin) -->
    <template v-else-if="showButtons">
      <!-- Transferir para agente (supervisor/admin) -->
      <div v-if="isSupervisor || isAdmin" class="relative">
        <button
          v-tooltip="'Transferir para agente'"
          class="centertel-hdr-btn text-violet-600 border-violet-300 hover:bg-violet-50"
          @click="showAgentsDropdown = !showAgentsDropdown; showTeamsDropdown = false; loadAgents()"
        >
          →
        </button>
        <div
          v-if="showAgentsDropdown"
          v-on-clickaway="() => (showAgentsDropdown = false)"
          class="absolute right-0 top-full mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg z-50 w-[220px] max-h-[280px] overflow-hidden"
        >
          <input
            v-model="agentSearch"
            type="text"
            placeholder="Buscar agente..."
            class="w-full px-3 py-2 text-xs border-b border-n-weak outline-none bg-n-background text-n-slate-12 placeholder:text-n-slate-8"
          />
          <div class="overflow-y-auto max-h-[220px]">
            <button
              v-for="agent in filteredAgents"
              :key="agent.id"
              class="flex items-center gap-2 w-full px-3 py-2 text-left text-xs hover:bg-n-alpha-1 transition-colors"
              @click="transferToAgent(agent.id)"
            >
              <span
                class="w-2 h-2 rounded-full flex-shrink-0"
                :class="agent.availability_status === 'online' ? 'bg-green-500' : 'bg-n-slate-8'"
              />
              <span class="truncate text-n-slate-12">{{ agent.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Transferir de time (supervisor/admin) -->
      <div v-if="isSupervisor || isAdmin" class="relative">
        <button
          v-tooltip="'Transferir de time'"
          class="centertel-hdr-btn text-blue-600 border-blue-300 hover:bg-blue-50"
          @click="showTeamsDropdown = !showTeamsDropdown; showAgentsDropdown = false; loadTeams()"
        >
          👥
        </button>
        <div
          v-if="showTeamsDropdown"
          v-on-clickaway="() => (showTeamsDropdown = false)"
          class="absolute right-0 top-full mt-1 bg-n-background border border-n-weak rounded-lg shadow-lg z-50 w-[220px] max-h-[280px] overflow-hidden"
        >
          <div class="overflow-y-auto max-h-[220px]">
            <button
              v-for="team in teams"
              :key="team.id"
              class="flex items-center gap-2 w-full px-3 py-2 text-left text-xs hover:bg-n-alpha-1 transition-colors"
              @click="transferToTeam(team.id)"
            >
              <span class="truncate text-n-slate-12">{{ team.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Devolver à fila (supervisor/admin, só em abertas com assignee) -->
      <button
        v-if="(isSupervisor || isAdmin) && hasAssignee && !isPending"
        v-tooltip="'Devolver à Fila'"
        class="centertel-hdr-btn text-amber-600 border-amber-300 hover:bg-amber-50"
        @click="returnToQueue"
      >
        ↩
      </button>

      <!-- Finalizar (supervisor/admin sempre, vendedor só nas suas) -->
      <button
        v-if="isSupervisor || isAdmin || (isVendor && isMine)"
        v-tooltip="'Finalizar Atendimento'"
        class="centertel-hdr-btn text-red-600 border-red-300 hover:bg-red-50"
        @click="resolveConversation"
      >
        ✓
      </button>
    </template>
  </div>
</template>

<style scoped>
.centertel-hdr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  background: transparent;
}
</style>
